import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import {
	getPatientFormToRender,
	uploadPatientFormResults,
	Form as FormType,
	FormElement,
	FieldResponse,
	FileResponse,
	getFormProgressPatient,
	saveFormProgressPatient,
	uploadPatientFormAttachment,
	AssignedForm,
} from "../../api/formApi";

import { Alert, Card, Typography, Space, Spin } from "antd";
import { CheckCircleOutlined, LoadingOutlined, StopOutlined } from "@ant-design/icons";

import { toFileResponses } from "../../utils/forms/formFileResponses";
import { isFormDone } from "../../utils/forms/formStatus";
import UnifiedFormRenderer, { flattenFields } from "../../components/forms/FormRenderer";
import HandoverExitGuard from "../../components/forms/HandoverExitGuard";
import {
	HANDOVER_MODE,
	HANDOVER_MODE_PARAM,
	exitHandover,
	readHandoverSession,
} from "../../utils/forms/handoverSession";
import type { UserUuid } from "../../types/identity";

const { Title, Text, Paragraph } = Typography;

/**
 * How long the patient sees "Form completed" before the device returns itself to the clinical
 * app. Long enough to read, short enough that the patient is never left holding a finished
 * form wondering what to do — they must never have to navigate back themselves.
 */
const HANDOVER_RETURN_DELAY_MS = 2500;

interface FormRendererProps {
	formId?: number;
	/**
	 * The assignment's access token. On the standalone page it comes off `?t=`; in the
	 * dashboard modal it arrives as this prop, because the patient's listing already carries
	 * it and there is no URL to read.
	 */
	accessToken?: string;
	isModal?: boolean;
	onComplete?: () => void;
}

const useQuery = (): URLSearchParams => new URLSearchParams(useLocation().search);

/**
 * Patient-facing form page/modal. Thin wrapper around the shared
 * `UnifiedFormRenderer` (compact, wizard) — it owns loading, progress
 * save/restore, file upload + submission, and the completion screen.
 */
const FormRenderer: React.FC<FormRendererProps> = ({ formId: propFormId, accessToken: propAccessToken, isModal = false, onComplete }) => {
	const [formData, setFormData] = useState<FormType | null>(null);
	const [assigned, setAssigned] = useState<AssignedForm>();
	const [responses, setResponses] = useState<Record<string | number, any>>({});
	const [uploadedFiles, setUploadedFiles] = useState<Record<string | number, File[]>>({});
	const [currentSectionIndex, setCurrentSectionIndex] = useState<number>(0);
	const [submitError, setSubmitError] = useState<string | null>(null);

	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [isSaving, setIsSaving] = useState<boolean>(false);
	// Starts true: the first paint happens before the fetch effect runs, and with `false`
	// that frame now falls through to the invalid-link view — a visible flash of
	// "this link is no longer valid" on a link that is perfectly good.
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [isComplete, setIsComplete] = useState<boolean>(false);
	const [isLinkInvalid, setIsLinkInvalid] = useState<boolean>(false);

	const query = useQuery();
	const urlId = query.get("id");
	const id = propFormId || (urlId ? parseInt(urlId, 10) : null);
	// Prop first, so the dashboard modal never picks up a stale `?t=` left on the URL by a
	// previous standalone visit.
	const accessToken = propAccessToken || query.get("t") || undefined;

	/**
	 * Hand-over (kiosk) mode — F10, ClickUp 86d3t4ev9.
	 *
	 * The mode lives in the URL, not in state, so it survives a reload: a patient who reloads
	 * mid-form is still handed over and still needs the PIN to leave. Never in the modal — the
	 * dashboard modal already sits inside the clinical shell, so there is nothing to guard.
	 */
	const isHandover = !isModal && query.get(HANDOVER_MODE_PARAM) === HANDOVER_MODE;

	useEffect(() => {
		const fetchFormData = async () => {
			// No id at all is the same dead end as a bad token, and must render the same way
			// rather than the blank page `if (!formData) return null` used to give.
			if (!id) {
				setIsLinkInvalid(true);
				setIsLoading(false);
				return;
			}

			setIsLoading(true);
			setIsLinkInvalid(false);
			try {
				const { form, assigned } = await getPatientFormToRender(id, accessToken);
				// `Reviewed` is a SUBMITTED form — a practitioner has read it. Testing only for
				// `Completed` sent the patient a blank fillable form for answers they had already
				// given, and a submission over it is accepted (`acceptSubmission()` writes
				// `Completed` from any status but `Completed`), so the reviewed submission was
				// overwritten. Re-opening a finished form is F6/S3's reopen route, not this branch.
				if (isFormDone(assigned.status)) {
					setIsComplete(true);
				} else {
					const parsedStructure = form.structure
						? (typeof form.structure === "string" ? JSON.parse(form.structure) : form.structure)
						: [];
					setFormData({ ...form, structure: parsedStructure });
					setAssigned(assigned);

					try {
						const progressData = await getFormProgressPatient(id, accessToken);
						if (progressData && progressData.progress) {
							const savedProgress = typeof progressData.progress === "string"
								? JSON.parse(progressData.progress)
								: progressData.progress;
							setResponses(savedProgress.responses || {});
							setCurrentSectionIndex(savedProgress.currentSection || 0);
						}
					} catch (err) {
						console.log("No saved progress found");
					}
				}
			} catch (error) {
				// `EnsureFormAccessToken` answers 404 for EVERY refusal — missing token, wrong
				// token, expired token, unknown id — deliberately, so an enumerator gets no
				// oracle. There is therefore nothing to distinguish here: one honest
				// "this link no longer works" state covers all of them.
				console.error("Error fetching form:", error);
				setIsLinkInvalid(true);
			} finally {
				setIsLoading(false);
			}
		};

		fetchFormData();
	}, [id, accessToken]);

	/**
	 * Post-submit return. The patient hands the device back; they must never be the one to
	 * navigate out of the form and into the clinic's app.
	 *
	 * Also fires when the form was ALREADY complete on load, which is the right outcome: there
	 * is nothing for the patient to do on a completed form either.
	 */
	useEffect(() => {
		if (!isHandover || !isComplete) return;
		const timer = setTimeout(() => {
			const session = readHandoverSession();
			exitHandover(session?.returnPath || "/");
		}, HANDOVER_RETURN_DELAY_MS);
		return () => clearTimeout(timer);
	}, [isHandover, isComplete]);

	const saveProgress = async (sectionIndex: number) => {
		if (!assigned) return;
		setIsSaving(true);
		try {
			const progressData = {
				responses,
				currentSection: sectionIndex,
				lastSaved: new Date().toISOString(),
			};
			await saveFormProgressPatient(assigned.id, JSON.stringify(progressData), accessToken);
		} catch (error) {
			console.error("Error saving progress:", error);
		} finally {
			setIsSaving(false);
		}
	};

	const handleSectionChange = (index: number) => {
		setCurrentSectionIndex(index);
		saveProgress(index);
	};

	const handleSubmit = async () => {
		if (!formData || !assigned) return;
		setSubmitError(null);
		setIsSubmitting(true);
		try {
			const allFields = flattenFields(formData.structure, !!formData.use_sections);

			const finalResponses: FieldResponse[] = await Promise.all(
				allFields.map(async (field: FormElement) => {
					const questionText = field.question || field.placeholder || `Field ${field.id}`;
					let files: FileResponse[] = [];

					if (field.type === "file") {
						const fieldFiles = uploadedFiles[field.id] || [];
						for (let i = 0; i < fieldFiles.length; i++) {
							// ⚠️ healthcare, NOT the file server (ClickUp 86d3y4e9p). This used to
							// call `uploadFiles`, which needs a signed-in session — a patient who
							// followed an emailed or QR link has none, so every upload 401'd and a
							// form with a file field was uncompletable by exactly the caller this
							// page serves. Routing through the tokenised endpoint also means the
							// SERVER decides which patient the file lands on, rather than trusting
							// `assigned.patient_id` off the rendered payload.
							const response = await uploadPatientFormAttachment(
								assigned.id as number,
								fieldFiles[i],
								accessToken,
							);
							// The envelope is still `{ files: [...] }` — and still carries no
							// `file_id`, which is what this mapper exists for.
							files.push(...toFileResponses(response, assigned.patient_id as UserUuid));
						}
					}

					return {
						id: field.id,
						type: field.type,
						question: questionText,
						response: field.type !== "file" ? responses[field.id] ?? null : files,
					};
				})
			);

			// `handover_device` matches healthcare's `in:` vocabulary byte for byte. ⚠️ The
			// patient endpoint does not read it yet — see `uploadPatientFormResults` — so the
			// stored row still says `patient_link` until healthcare is changed to accept it.
			await uploadPatientFormResults(
				assigned.id as number,
				JSON.stringify(finalResponses),
				accessToken,
				isHandover ? "handover_device" : undefined,
			);
			setIsComplete(true);

			if (isModal && onComplete) {
				setTimeout(() => onComplete(), 2000);
			}
		} catch (error: unknown) {
			console.error(error);
			setSubmitError("Failed to submit form. Please try again.");
		} finally {
			setIsSubmitting(false);
		}
	};

	/**
	 * Wrap every exit of this component so hand-over mode gets its guard — including the
	 * loading, invalid-link and completed screens.
	 *
	 * That breadth is the point: a token that turns out to be dead, or a form that was already
	 * submitted, must not strand the practitioner on a chrome-free dead end with no way back.
	 * The guard is a property of the device state, not of the form loading successfully.
	 */
	const withHandover = (node: React.ReactNode) =>
		isHandover ? (
			<div data-testid="handover-form-root">
				{node}
				<HandoverExitGuard assignedFormId={id} />
			</div>
		) : (
			<>{node}</>
		);

	// Clears the fixed "Return to clinic" button in the top-right corner. Only in hand-over
	// mode — the emailed patient link has no such button and must not gain dead space.
	const handoverTopPad = isHandover ? { paddingTop: 64 } : {};

	if (isLoading) {
		return withHandover(
			<div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}>
				<Spin size="large" />
			</div>,
		);
	}

	// Centred card on the standalone page (no <Layout> here — this route renders chrome-free),
	// plain padded block inside the dashboard modal. Shared by the completion and
	// invalid-link states so the two look like the same product.
	const outcomeContainerStyle = isModal
		? { textAlign: "center" as const, padding: 24 }
		: { display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", backgroundColor: "#f5f5f5", padding: 16, ...handoverTopPad };

	const invalidInner = (
		<>
			<StopOutlined style={{ fontSize: 64, color: "#bfbfbf", marginBottom: 16 }} />
			<Title level={3}>This form link is no longer valid</Title>
			<Paragraph type="secondary">
				The link may have expired, or it may have been replaced by a newer one. Please
				contact your clinic and ask them to send the form again.
			</Paragraph>
		</>
	);

	const invalidLinkView = (
		<div style={outcomeContainerStyle} data-testid="patient-form-invalid-link">
			{isModal ? invalidInner : <Card style={{ maxWidth: 500, textAlign: "center" as const }}>{invalidInner}</Card>}
		</div>
	);

	if (isLinkInvalid) return withHandover(invalidLinkView);

	if (isComplete) {
		const completionInner = (
			<>
				<CheckCircleOutlined style={{ fontSize: 64, color: "#52c41a", marginBottom: 16 }} />
				<Title level={3}>Form Completed</Title>
				<Paragraph type="secondary">
					{isHandover
						? "Thank you. Your responses have been submitted — please hand the device back."
						: "Thank you for completing the form. Your responses have been submitted."}
				</Paragraph>
			</>
		);

		return withHandover(
			<div style={outcomeContainerStyle} data-testid="patient-form-complete">
				{isModal ? completionInner : <Card style={{ maxWidth: 500, textAlign: "center" as const }}>{completionInner}</Card>}
			</div>,
		);
	}

	// Loaded, not complete, and still nothing to render — a malformed payload. It used to
	// return null, i.e. a blank white page with no explanation. Same honest dead end as a
	// refused token.
	if (!formData) return withHandover(invalidLinkView);

	const containerStyle = isModal
		? { padding: 0 }
		: { display: "flex", alignItems: "center", justifyContent: "center", padding: 16, minHeight: "100vh", backgroundColor: "#f5f5f5", ...handoverTopPad };

	const cardStyle = isModal
		? { width: "100%", border: "none", boxShadow: "none" }
		: { width: "100%", maxWidth: 700 };

	// `patient-form-root` is the e2e anchor for "the form actually rendered" (F13). The other
	// three outcomes already had one (`patient-form-invalid-link`, `patient-form-complete`,
	// `handover-form-root`); without this the success path was the only state a browser test
	// could not name, and "not the invalid-link screen" is not the same assertion.
	return withHandover(
		<div style={containerStyle} data-testid="patient-form-root">
			<Card style={cardStyle}>
				<div style={{ paddingBottom: 12, borderBottom: "1px solid #f0f0f0", marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
					<Title level={4} style={{ margin: 0 }}>
						{formData.title}
					</Title>
					{isSaving && (
						<Space size="small" style={{ fontSize: 11, color: "#8c8c8c" }}>
							<LoadingOutlined />
							<span>Saving…</span>
						</Space>
					)}
				</div>

				<UnifiedFormRenderer
					structure={formData.structure}
					useSections={!!formData.use_sections}
					values={responses}
					onValuesChange={setResponses}
					files={uploadedFiles}
					onFilesChange={setUploadedFiles}
					sectionMode="wizard"
					initialSection={currentSectionIndex}
					onSectionChange={handleSectionChange}
					onSubmit={handleSubmit}
					submitting={isSubmitting}
					submitLabel="Submit Form"
					submitTestId="patient-form-submit"
				/>

				{submitError && (
					<Alert message={submitError} type="error" showIcon style={{ marginTop: 12, fontSize: 12 }} />
				)}
			</Card>
		</div>,
	);
};

export default FormRenderer;
